// ══════════════════════════════════════════════════════════════════════════════
// BookClubPage.js — Gita Book Club
// 4-week themed clubs with strangers. Discussion, not therapy.
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffff;
  const colors = ["#e8832a","#c9953a","#7c3aed","#2563eb","#059669","#dc2626","#d97706","#0891b2"];
  return colors[h % colors.length];
}

const THEME_ACCENTS = {
  karma:         "#e8832a",
  fear:          "#ef4444",
  grief:         "#6366f1",
  purpose:       "#f59e0b",
  relationships: "#ec4899",
  ego:           "#8b5cf6",
};

const THEMES = [
  { id: "karma",         label: "Karma",         desc: "Action without attachment",      icon: "⚙️" },
  { id: "fear",          label: "Fear & Courage", desc: "Facing what paralyses you",      icon: "🔥" },
  { id: "grief",         label: "Grief & Loss",   desc: "What the Gita says about death", icon: "🌧" },
  { id: "purpose",       label: "Purpose",        desc: "Dharma and your life's calling", icon: "🌟" },
  { id: "relationships", label: "Relationships",  desc: "Attachment, love, letting go",   icon: "🌸" },
  { id: "ego",           label: "Ego & Identity", desc: "Who are you beyond your roles?", icon: "🪷" },
];

const WEEK_MODES = {
  1: { label: "Week 1 — Read",      desc: "5 verses on this theme. Read silently. No discussion yet. Sit with it.",                                icon: "📖" },
  2: { label: "Week 2 — Question",  desc: "Submit one real-life question this theme raises. Group votes. Gita answers. Everyone discusses.",       icon: "💬" },
  3: { label: "Week 3 — Try It",    desc: "One practical challenge from the Gita. Share one line: what happened?",                                icon: "🌿" },
  4: { label: "Week 4 — Reflect",   desc: "Complete: 'Before this club I thought ___ . Now I think ___.' Compiled as your group's understanding.", icon: "✨" },
};

// ── No club yet ────────────────────────────────────────────────────────────────
function JoinClub({ user, onJoined }) {
  const [step,     setStep]     = useState("theme"); // theme → confirm → waiting
  const [theme,    setTheme]    = useState(null);
  const [joining,  setJoining]  = useState(false);
  const [error,    setError]    = useState("");

  const handleJoin = async () => {
    if (!theme) return;
    setJoining(true);
    try {
      const res = await fetch(`${API}/bookclub/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, display_name: user.name, theme }),
      });
      const d = await res.json();
      if (d.error) { setError(d.error); }
      else { onJoined(d); }
    } catch { setError("Could not connect to server"); }
    setJoining(false);
  };

  if (step === "theme") return (
    <div>
      <div className="section-title">Gita Book Club</div>
      <div className="section-sub">4 weeks. 6–8 strangers. One theme. The Gita as a text — not therapy.</div>

      <div style={{ marginBottom: "28px" }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text)", marginBottom: "18px" }}>
          Choose your theme
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: "14px" }}>
          {THEMES.map(t => {
            const accent = THEME_ACCENTS[t.id] || "var(--saffron)";
            const sel = theme === t.id;
            return (
              <div key={t.id} onClick={() => setTheme(t.id)} style={{
                background: "#ffffff",
                border: `1px solid ${sel ? accent : "var(--border)"}`,
                borderRadius: "16px", padding: "20px 18px 18px",
                cursor: "pointer", transition: "all 0.2s",
                position: "relative", overflow: "hidden",
                boxShadow: sel ? "var(--card-shadow-hover)" : "var(--card-shadow)",
              }}>
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: accent,
                }} />
                <div style={{ fontSize: "2.2rem", marginBottom: "10px" }}>{t.icon}</div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: sel ? accent : "var(--text)", marginBottom: "6px" }}>
                  {t.label}
                </div>
                <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: 1.55 }}>{t.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How it works */}
      <div style={{ background: "#ffffff", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px", marginBottom: "24px", position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "var(--saffron)" }} />
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text)", marginBottom: "18px" }}>
          How it works
        </div>
        {Object.entries(WEEK_MODES).map(([w, info]) => (
          <div key={w} style={{ display: "flex", gap: "14px", marginBottom: "16px" }}>
            <span style={{ fontSize: "1.4rem", flexShrink: 0 }}>{info.icon}</span>
            <div>
              <div style={{ fontSize: "1.05rem", color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontWeight: 700, marginBottom: "4px" }}>{info.label}</div>
              <div style={{ fontSize: "0.97rem", color: "var(--text-dim)", lineHeight: 1.65 }}>{info.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {error && <div style={{ color: "#dc2626", marginBottom: "12px", fontSize: "0.95rem" }}>{error}</div>}

      <button
        onClick={() => theme && handleJoin()}
        disabled={!theme || joining}
        style={{
          padding: "12px 32px", borderRadius: "10px",
          background: theme ? "var(--saffron)" : "var(--bg3)",
          border: "none", color: theme ? "white" : "var(--text-dim)",
          fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem",
          cursor: theme ? "pointer" : "not-allowed",
        }}
      >
        {joining ? "Joining..." : theme ? `Join ${THEMES.find(t=>t.id===theme)?.label} Club →` : "Select a theme first"}
      </button>
    </div>
  );

  return null;
}

// ── Week 1: Read ──────────────────────────────────────────────────────────────
function Week1Read({ club, user, onRefresh, onOpenVerse }) {
  const [acking, setAcking] = useState(false);

  const acknowledge = async (e, chapter, verse) => {
    e.stopPropagation();
    setAcking(true);
    await fetch(`${API}/bookclub/${club.club_id}/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, chapter, verse }),
    });
    await onRefresh();
    setAcking(false);
  };

  const myReads = new Set((club.my_reads || []).map(r => `${r.chapter}-${r.verse}`));

  return (
    <div>
      <div style={{ color: "var(--text-dim)", fontSize: "0.95rem", marginBottom: "20px", lineHeight: 1.7 }}>
        Read each verse slowly. Tap a card to open the full commentary. Tap 🙏 when you've sat with it.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {(club.verses || []).map((v, i) => {
          const key = `${v.chapter}-${v.verse}`;
          const read = myReads.has(key);
          return (
            <div key={key}
              onClick={() => onOpenVerse && onOpenVerse(v.chapter, v.verse, club.theme)}
              style={{
                background: "#ffffff",
                border: `1px solid ${read ? "var(--saffron)" : "var(--border)"}`,
                borderRadius: "14px", padding: "20px",
                cursor: "pointer", transition: "all 0.2s",
                boxShadow: "var(--card-shadow)",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--saffron)"; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = read ? "var(--saffron)" : "var(--border)"; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ fontSize: "1rem", color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontWeight: 700 }}>
                  BG {v.chapter}.{v.verse}
                </div>
                <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>Tap for commentary →</div>
              </div>
              {v.sanskrit && (
                <div style={{ fontFamily: "'Noto Sans Devanagari',serif", fontSize: "1.15rem", color: "var(--text)", lineHeight: 1.8, marginBottom: "12px" }}>
                  {v.sanskrit}
                </div>
              )}
              <div style={{ fontSize: "1.1rem", color: "var(--text)", lineHeight: 1.85, marginBottom: "16px" }}>
                {v.meaning}
              </div>
              <button
                onClick={(e) => !read && !acking && acknowledge(e, v.chapter, v.verse)}
                disabled={read || acking}
                style={{
                  padding: "8px 20px", borderRadius: "8px",
                  background: read ? "#ffffff" : "var(--saffron)",
                  border: read ? "1px solid var(--saffron)" : "none",
                  color: read ? "var(--saffron)" : "white",
                  fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem",
                  cursor: read ? "default" : "pointer",
                }}
              >
                {read ? "🙏 Read" : "🙏 I've read this"}
              </button>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: "20px", padding: "14px 16px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "10px", fontSize: "0.95rem", color: "var(--text-dim)" }}>
        {myReads.size}/{(club.verses || []).length} verses read · Discussion opens in Week 2
      </div>
    </div>
  );
}

// ── Week 2: Question ──────────────────────────────────────────────────────────
function Week2Question({ club, user, onRefresh }) {
  const [question,   setQuestion]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [voting,     setVoting]     = useState(false);
  const [replyText,  setReplyText]  = useState({});
  const [replying,   setReplying]   = useState(null);

  const submitQ = async () => {
    if (!question.trim()) return;
    setSubmitting(true);
    await fetch(`${API}/bookclub/${club.club_id}/question`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, display_name: user.name, question: question.trim() }),
    });
    setQuestion("");
    await onRefresh();
    setSubmitting(false);
  };

  const vote = async (qid) => {
    setVoting(true);
    await fetch(`${API}/bookclub/${club.club_id}/vote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, question_id: qid }),
    });
    await onRefresh();
    setVoting(false);
  };

  const submitAnswer = async (qid) => {
    const txt = replyText[qid]?.trim();
    if (!txt || replying) return;
    setReplying(qid);
    await fetch(`${API}/bookclub/${club.club_id}/answer/${qid}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, display_name: user.name, text: txt }),
    });
    setReplyText(r => ({...r, [qid]: ""}));
    await onRefresh();
    setReplying(null);
  };

  const myVote = club.my_vote;
  const questions = club.questions || [];
  const topQ = questions.find(q => q.gita_answer);

  return (
    <div>
      {/* Top answered question */}
      {topQ && (
        <div style={{ background: "#ffffff", border: "1px solid var(--border)", borderRadius: "14px", padding: "20px", marginBottom: "20px", boxShadow: "var(--card-shadow)" }}>
          <div style={{ fontSize: "0.95rem", color: "var(--saffron)", fontWeight: 700, marginBottom: "12px", fontFamily: "'Inter',sans-serif" }}>
            This week's question
          </div>
          <div style={{ color: "var(--text-dim)", marginBottom: "12px" }}>"{topQ.question}"</div>
          <div style={{ fontSize: "0.95rem", color: "var(--text)", lineHeight: 1.7, borderLeft: "2px solid var(--saffron)", paddingLeft: "12px" }}>
            {topQ.gita_answer}
          </div>
          {topQ.chapter && (
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "8px" }}>— BG {topQ.chapter}.{topQ.verse}</div>
          )}
        </div>
      )}

      {/* Submit question */}
      {!club.my_question && (
        <div style={{ marginBottom: "20px" }}>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "10px", lineHeight: 1.6 }}>
            What real-life question does this theme raise for you? The group votes for the most interesting one.
          </div>
          <textarea value={question} onChange={e => setQuestion(e.target.value)}
            placeholder="What question does karma raise in your own life?"
            rows={3} style={{
              width: "100%", background: "#ffffff", border: "1px solid var(--border)",
              borderRadius: "8px", padding: "10px 14px", color: "var(--text)",
              fontFamily: "'Inter',sans-serif", fontSize: "1rem",
              outline: "none", resize: "none", boxSizing: "border-box", marginBottom: "10px",
            }} />
          <button onClick={submitQ} disabled={submitting || !question.trim()} style={{
            padding: "9px 22px", borderRadius: "8px",
            background: "var(--saffron)",
            border: "none", color: "white",
            fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
          }}>
            {submitting ? "Submitting..." : "Submit question →"}
          </button>
        </div>
      )}

      {/* All questions — voting + discussion */}
      {questions.length > 0 && (
        <div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--text)", marginBottom: "12px" }}>
            Group discussion
          </div>
          {questions.map(q => (
            <div key={q.id} style={{
              background: "#ffffff", border: `1px solid ${myVote === q.id ? "var(--saffron)" : "var(--border)"}`,
              borderRadius: "12px", padding: "16px", marginBottom: "14px",
              boxShadow: "var(--card-shadow)",
            }}>
              {/* Question + vote */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "12px" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "4px", fontWeight: 700 }}>{q.display_name}</div>
                  <div style={{ color: "var(--text-dim)", fontSize: "0.95rem" }}>"{q.question}"</div>
                </div>
                {!q.gita_answer && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                    <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>{q.votes}</span>
                    <button onClick={() => !myVote && vote(q.id)} disabled={!!myVote || voting} style={{
                      padding: "4px 10px", borderRadius: "6px",
                      background: myVote === q.id ? "var(--saffron)" : "transparent",
                      border: "1px solid var(--border)",
                      color: myVote === q.id ? "white" : "var(--text-dim)",
                      fontSize: "0.95rem", cursor: myVote ? "default" : "pointer",
                    }}>{myVote === q.id ? "✓" : "Vote"}</button>
                  </div>
                )}
              </div>

              {/* Answers / discussion */}
              {(q.answers || []).map(a => (
                <div key={a.id} style={{
                  marginLeft: "12px", borderLeft: `2px solid ${a.is_ai ? "var(--saffron)" : "var(--border)"}`,
                  paddingLeft: "10px", marginBottom: "10px",
                }}>
                  <div style={{ fontSize: "0.95rem", color: a.is_ai ? "var(--saffron)" : "var(--text-dim)", marginBottom: "3px", fontWeight: 700 }}>
                    {a.is_ai ? "🕉️ The Gita" : a.display_name}
                  </div>
                  <div style={{ fontSize: "0.95rem", color: "var(--text)", lineHeight: 1.6 }}>{a.text}</div>
                </div>
              ))}

              {/* Reply input */}
              <div style={{ marginLeft: "12px", marginTop: "10px", display: "flex", gap: "8px" }}>
                <input
                  value={replyText[q.id] || ""}
                  onChange={e => setReplyText(r => ({...r, [q.id]: e.target.value}))}
                  onKeyDown={e => e.key === "Enter" && submitAnswer(q.id)}
                  placeholder="Share your thoughts..."
                  style={{
                    flex: 1, background: "#ffffff", border: "1px solid var(--border)",
                    borderRadius: "6px", padding: "7px 12px", color: "var(--text)",
                    fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", outline: "none",
                  }}
                />
                <button onClick={() => submitAnswer(q.id)} disabled={replying === q.id || !replyText[q.id]?.trim()} style={{
                  padding: "7px 14px", borderRadius: "6px",
                  background: "var(--saffron)",
                  border: "none", color: "white", fontSize: "0.95rem", cursor: "pointer",
                }}>
                  {replying === q.id ? "..." : "→"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Week 3: Try It ────────────────────────────────────────────────────────────
function Week3TryIt({ club, user, onRefresh }) {
  const [share,      setShare]      = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!share.trim()) return;
    setSubmitting(true);
    await fetch(`${API}/bookclub/${club.club_id}/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, display_name: user.name, text: share.trim() }),
    });
    setShare("");
    await onRefresh();
    setSubmitting(false);
  };

  return (
    <div>
      {club.challenge && (
        <div style={{ background: "#ffffff", border: "1px solid var(--border)", borderRadius: "14px", padding: "20px", marginBottom: "20px", boxShadow: "var(--card-shadow)" }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "10px" }}>
            This week's challenge
          </div>
          <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: 1.7 }}>{club.challenge}</div>
        </div>
      )}

      {!club.my_share && (
        <div style={{ marginBottom: "20px" }}>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "10px" }}>Share one line — what happened when you tried it?</div>
          <textarea value={share} onChange={e => setShare(e.target.value)}
            placeholder="I tried it and..."
            rows={2} style={{
              width: "100%", background: "#ffffff", border: "1px solid var(--border)",
              borderRadius: "8px", padding: "10px 14px", color: "var(--text)",
              fontFamily: "'Inter',sans-serif", fontSize: "1rem",
              outline: "none", resize: "none", boxSizing: "border-box", marginBottom: "10px",
            }} />
          <button onClick={submit} disabled={submitting || !share.trim()} style={{
            padding: "9px 22px", borderRadius: "8px",
            background: "var(--saffron)",
            border: "none", color: "white",
            fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
          }}>
            {submitting ? "Sharing..." : "Share →"}
          </button>
        </div>
      )}

      {(club.shares || []).length > 0 && (
        <div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--text)", marginBottom: "12px" }}>
            What your group tried
          </div>
          {club.shares.map((s, i) => (
            <div key={i} style={{ borderLeft: "2px solid var(--saffron)", paddingLeft: "12px", marginBottom: "12px" }}>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "4px", fontWeight: 700 }}>{s.display_name}</div>
              <div style={{ fontSize: "0.95rem", color: "var(--text)" }}>"{s.text}"</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Week 4: Reflect ───────────────────────────────────────────────────────────
function Week4Reflect({ club, user, onRefresh }) {
  const [before,     setBefore]     = useState("");
  const [after,      setAfter]      = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied,     setCopied]     = useState(false);

  const submit = async () => {
    if (!before.trim() || !after.trim()) return;
    setSubmitting(true);
    await fetch(`${API}/bookclub/${club.club_id}/reflect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, display_name: user.name, before: before.trim(), after: after.trim() }),
    });
    setBefore(""); setAfter("");
    await onRefresh();
    setSubmitting(false);
  };

  const theme = THEMES.find(t => t.id === club.theme);
  const reflections = club.reflections || [];
  const memberCount = (club.members || []).length;
  const shareText = [
    `${theme?.icon} ${theme?.label} — Gita Book Club`,
    `${memberCount} seekers · 4 weeks · The Bhagavad Gita`,
    "",
    ...reflections.map(r => `"Before: ${r.before}"\n"Now: ${r.after}"\n— ${r.display_name}`),
    "",
    "🕉 VedSanga",
  ].join("\n");

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ text: shareText }); return; } catch {}
    }
    await navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div>
      {!club.my_reflection && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "16px", lineHeight: 1.7 }}>
            Complete this sentence to close your journey:
          </div>
          <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "6px", fontWeight: 700 }}>Before this club I thought...</div>
            <input value={before} onChange={e => setBefore(e.target.value)} placeholder="..."
              style={{ width: "100%", background: "#ffffff", border: "1px solid var(--border)", borderRadius: "8px", padding: "10px 14px", color: "var(--text)", fontFamily: "'Inter',sans-serif", fontSize: "1rem", outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "6px", fontWeight: 700 }}>Now I think...</div>
            <input value={after} onChange={e => setAfter(e.target.value)} placeholder="..."
              style={{ width: "100%", background: "#ffffff", border: "1px solid var(--border)", borderRadius: "8px", padding: "10px 14px", color: "var(--text)", fontFamily: "'Inter',sans-serif", fontSize: "1rem", outline: "none", boxSizing: "border-box" }} />
          </div>
          <button onClick={submit} disabled={submitting || !before.trim() || !after.trim()} style={{
            padding: "9px 22px", borderRadius: "8px",
            background: "var(--saffron)",
            border: "none", color: "white",
            fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
          }}>
            {submitting ? "Saving..." : "Submit reflection →"}
          </button>
        </div>
      )}

      {reflections.length > 0 && (
        <div>
          {/* Collective Understanding Card — the viral moment */}
          <div style={{
            background: "#ffffff",
            border: "1px solid var(--border)",
            borderRadius: "18px", overflow: "hidden", marginBottom: "16px",
            boxShadow: "var(--card-shadow)",
          }}>
            {/* Saffron top bar */}
            <div style={{ height: "3px", background: "var(--saffron)" }} />

            {/* Card header */}
            <div style={{
              padding: "24px 24px 18px",
              textAlign: "center",
              background: "var(--bg2)",
              borderBottom: "1px solid var(--border)",
            }}>
              <div style={{ fontSize: "2.4rem", marginBottom: "8px" }}>
                {theme?.icon}
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.15rem", color: "var(--saffron)", marginBottom: "6px" }}>
                {theme?.label}
              </div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                {memberCount} seekers · 4 weeks · The Bhagavad Gita
              </div>
            </div>

            {/* Reflections */}
            <div style={{ padding: "8px 0" }}>
              {reflections.map((r, i) => (
                <div key={i} style={{
                  padding: "16px 22px",
                  borderBottom: i < reflections.length - 1 ? "1px solid var(--border)" : "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: "50%",
                      background: avatarColor(r.display_name),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "0.65rem", color: "white", fontWeight: "bold", flexShrink: 0,
                    }}>
                      {r.display_name?.[0]?.toUpperCase()}
                    </div>
                    <span style={{ fontSize: "0.95rem", color: "var(--text-dim)", fontWeight: 700 }}>{r.display_name}</span>
                  </div>
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: 1.5, marginBottom: "5px" }}>
                    Before: "{r.before}"
                  </div>
                  <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: 1.6 }}>
                    Now: "{r.after}"
                  </div>
                </div>
              ))}
            </div>

            {/* Card footer */}
            <div style={{
              padding: "12px 24px", background: "var(--bg2)",
              borderTop: "1px solid var(--border)",
              display: "flex", justifyContent: "center", alignItems: "center", gap: "8px",
            }}>
              <span style={{ color: "var(--saffron)", fontSize: "0.95rem" }}>🕉</span>
              <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "var(--saffron)" }}>
                VedSanga
              </span>
            </div>
          </div>

          {reflections.length >= 3 && (
            <button onClick={handleShare} style={{
              width: "100%", padding: "12px",
              borderRadius: "10px",
              background: copied ? "#ffffff" : "#ffffff",
              border: `1px solid ${copied ? "#15803d" : "var(--saffron)"}`,
              color: copied ? "#15803d" : "var(--saffron)",
              fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem",
              cursor: "pointer", transition: "all 0.3s",
            }}>
              {copied ? "✓ Copied to clipboard" : "📱 Share this moment"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Verse Detail (full-page within Book Club) ─────────────────────────────────
function VerseDetail({ chapter, verse, theme, onBack }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/shloka/commentary/${chapter}/${verse}?theme=${theme || ""}`);
        const d = await res.json();
        setData(d);
      } catch {}
      setLoading(false);
    })();
  }, [chapter, verse, theme]);

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-dim)", fontFamily: "'Inter',sans-serif", fontSize: "1rem", padding: "0", marginBottom: "20px" }}>
        ← Back to club
      </button>

      <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "16px" }}>
        Bhagavad Gita · {chapter}.{verse}
      </div>

      {loading && (
        <div style={{ color: "var(--text-dim)", padding: "40px 0", textAlign: "center" }}>
          🕉️ Loading verse...
        </div>
      )}

      {data && (
        <div>
          {/* Sanskrit */}
          {data.sanskrit && (
            <div style={{ fontFamily: "'Noto Sans Devanagari',serif", fontSize: "1.4rem", color: "var(--text)", lineHeight: 1.9, marginBottom: "10px" }}>
              {data.sanskrit}
            </div>
          )}
          {/* Transliteration */}
          {data.transliteration && (
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: 1.6, marginBottom: "18px" }}>
              {data.transliteration}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "18px 0" }}>
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
            <div style={{ width: 8, height: 8, background: "var(--saffron)", transform: "rotate(45deg)" }} />
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
          </div>

          {/* Meaning */}
          {data.meaning && (
            <div style={{ fontSize: "1.15rem", lineHeight: 2.0, color: "var(--text)", marginBottom: "24px" }}>
              {data.meaning}
            </div>
          )}

          {/* Story / Commentary */}
          {data.story && (
            <div style={{ background: "#ffffff", border: "1px solid var(--border)", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "var(--card-shadow)" }}>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "12px" }}>Commentary</div>
              <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: 1.8 }}>{data.story}</div>
            </div>
          )}

          {/* Reflection */}
          {data.reflection && (
            <div style={{ borderLeft: "3px solid var(--saffron)", paddingLeft: "16px", fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: 1.7, marginBottom: "16px" }}>
              🪷 {data.reflection}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Club View ─────────────────────────────────────────────────────────────────
function ClubView({ clubId, user, onLeave }) {
  const [club,       setClub]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [openVerse,  setOpenVerse]  = useState(null); // { chapter, verse, theme }

  const load = async () => {
    try {
      const res = await fetch(`${API}/bookclub/${clubId}/${encodeURIComponent(user.id)}`);
      const d = await res.json();
      setClub(d);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [clubId]); // eslint-disable-line

  if (loading) return <div style={{ color: "var(--text-dim)", padding: "40px", textAlign: "center" }}>🕉️ Loading club...</div>;
  if (!club || club.error) return <div style={{ color: "var(--text-dim)", padding: "40px" }}>Club not found.</div>;

  if (openVerse) return (
    <VerseDetail
      chapter={openVerse.chapter}
      verse={openVerse.verse}
      theme={openVerse.theme}
      onBack={() => setOpenVerse(null)}
    />
  );

  const theme = THEMES.find(t => t.id === club.theme);
  const weekMode = WEEK_MODES[club.week_number] || WEEK_MODES[1];
  const memberCount = (club.members || []).length;

  const accent = THEME_ACCENTS[club.theme] || "#e8832a";

  return (
    <div>
      <button onClick={onLeave} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-dim)", fontFamily: "'Inter',sans-serif", fontSize: "1rem", padding: "0", marginBottom: "20px" }}>
        ← Book Clubs
      </button>

      {/* Hero header */}
      <div style={{
        background: "#ffffff",
        border: "1px solid var(--border)",
        borderLeft: `4px solid ${accent}`,
        borderRadius: "18px", padding: "24px",
        marginBottom: "20px", position: "relative", overflow: "hidden",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div>
            <div style={{ fontSize: "2.2rem", marginBottom: "6px" }}>
              {theme?.icon}
            </div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.2rem", color: accent, marginBottom: "4px" }}>
              {theme?.label}
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
              {memberCount} seekers · Week {club.week_number} of 4
            </div>
          </div>
          <div style={{
            background: "var(--bg2)", border: `1px solid var(--border)`,
            borderRadius: "20px", padding: "5px 14px",
            fontSize: "0.9rem", color: accent,
            fontFamily: "'Inter',sans-serif", fontWeight: 700, flexShrink: 0,
          }}>
            {weekMode.icon} {weekMode.label}
          </div>
        </div>

        {/* Member avatars */}
        <div style={{ display: "flex", alignItems: "center" }}>
          {(club.members || []).slice(0, 8).map((m, i) => (
            <div key={i} title={m.display_name} style={{
              width: 30, height: 30, borderRadius: "50%",
              background: avatarColor(m.display_name),
              border: "2px solid #ffffff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.72rem", color: "white", fontWeight: "bold",
              marginLeft: i > 0 ? "-7px" : 0,
              zIndex: 10 - i, position: "relative",
            }}>
              {m.display_name?.[0]?.toUpperCase()}
            </div>
          ))}
          <div style={{ marginLeft: "14px", fontSize: "0.95rem", color: "var(--text-dim)" }}>
            {memberCount} anonymous seekers
          </div>
        </div>
      </div>

      {/* Week mode description */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "10px", padding: "14px 16px", marginBottom: "20px", fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: 1.6 }}>
        {weekMode.desc}
      </div>

      {/* Week content */}
      {club.week_number === 1 && <Week1Read club={club} user={user} onRefresh={load} onOpenVerse={(ch, vs, th) => setOpenVerse({ chapter: ch, verse: vs, theme: th })} />}
      {club.week_number === 2 && <Week2Question club={club} user={user} onRefresh={load} />}
      {club.week_number === 3 && <Week3TryIt club={club} user={user} onRefresh={load} />}
      {club.week_number === 4 && <Week4Reflect club={club} user={user} onRefresh={load} />}

      {club.status === "forming" && (() => {
        const start = new Date(club.start_date + "T00:00:00");
        const today = new Date(); today.setHours(0,0,0,0);
        const daysLeft = Math.round((start - today) / 86400000);
        const countdownText = daysLeft === 0 ? "Starting today!"
          : daysLeft === 1 ? "Starts tomorrow"
          : `Starts in ${daysLeft} days`;
        return (
          <div style={{ marginTop: "20px", background: "#ffffff",
            border: "1px solid var(--border)", borderRadius: "14px",
            padding: "20px 18px", textAlign: "center", boxShadow: "var(--card-shadow)" }}>
            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>🕯️</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem",
              color: "var(--saffron)", marginBottom: "6px" }}>
              {countdownText}
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: 1.7,
              marginBottom: "10px" }}>
              <strong style={{ color: "var(--text)" }}>{club.member_count || 1}</strong> seeker{(club.member_count||1) !== 1 ? "s have" : " has"} joined this cohort.
              All members begin Week 1 together on <strong style={{ color: "var(--saffron)" }}>
                {start.toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long" })}
              </strong>.
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
              No one can join after the club starts — everyone reads the same verse on the same day.
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN BookClubPage
// ══════════════════════════════════════════════════════════════════════════════
export default function BookClubPage({ user }) {
  const [clubs,       setClubs]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeClub,  setActiveClub]  = useState(null);

  const loadClubs = async () => {
    try {
      const res = await fetch(`${API}/bookclub/my/${encodeURIComponent(user.id)}`);
      const d = await res.json();
      setClubs(d.clubs || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadClubs(); }, [user.id]); // eslint-disable-line

  if (loading) return (
    <div className="page">
      <div style={{ color: "var(--text-dim)", padding: "60px", textAlign: "center" }}>
        🕉️ Loading your clubs...
      </div>
    </div>
  );

  if (activeClub) return (
    <div className="page">
      <ClubView clubId={activeClub} user={user} onLeave={() => { setActiveClub(null); loadClubs(); }} />
    </div>
  );

  return (
    <div className="page">
      {clubs.length === 0 ? (
        <JoinClub user={user} onJoined={d => { loadClubs(); if (d.club_id) setActiveClub(d.club_id); }} />
      ) : (
        <div>
          <div className="section-title">Gita Book Club</div>
          <div className="section-sub">4 weeks · 6–8 strangers · One theme · The Gita as a text</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: "14px", marginTop: "24px" }}>
            {clubs.map(c => {
              const theme = THEMES.find(t => t.id === c.theme);
              const accent = THEME_ACCENTS[c.theme] || "#e8832a";
              const weekLabel = c.status === "forming"
                ? `Forming · Starts ${c.start_date}`
                : `Week ${c.week_number} of 4 · ${c.member_count} seekers`;
              return (
                <div key={c.club_id} onClick={() => setActiveClub(c.club_id)} style={{
                  background: "#ffffff",
                  border: "1px solid var(--border)",
                  borderLeft: `4px solid ${accent}`,
                  borderRadius: "16px", padding: "22px 20px",
                  cursor: "pointer", transition: "all 0.2s", position: "relative", overflow: "hidden",
                  boxShadow: "var(--card-shadow)",
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "var(--card-shadow)"; e.currentTarget.style.transform = "none"; }}
                >
                  <div style={{ fontSize: "2rem", marginBottom: "10px" }}>{theme?.icon}</div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: accent, marginBottom: "5px" }}>{theme?.label}</div>
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: 1.5, marginBottom: "14px" }}>{theme?.desc}</div>
                  <div style={{
                    display: "inline-block",
                    background: "var(--bg2)", border: "1px solid var(--border)",
                    borderRadius: "20px", padding: "3px 10px",
                    fontSize: "0.9rem", color: accent, fontWeight: 700,
                  }}>
                    {weekLabel}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={() => setClubs([])} style={{
            marginTop: "20px", padding: "9px 22px", borderRadius: "8px",
            background: "transparent", border: "1px solid var(--saffron)",
            color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontWeight: 700,
            fontSize: "0.95rem", cursor: "pointer",
          }}>
            + Join another club
          </button>
        </div>
      )}
    </div>
  );
}
